import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Cookie, Shield, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';

const CookiesPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'it';

  const content = {
    it: {
      title: "Cookie Policy",
      lastUpdate: "Ultimo aggiornamento: Dicembre 2025",
      intro: `Questa Cookie Policy spiega cosa sono i cookie, quali cookie utilizziamo su SmartBalloon e come puoi gestirli.`,
      sections: [
        {
          title: "Cosa Sono i Cookie",
          content: `I cookie sono piccoli file di testo che vengono memorizzati sul tuo dispositivo (computer, tablet, smartphone) quando visiti un sito web. Servono a ricordare le tue preferenze, mantenere la sessione attiva e migliorare la tua esperienza di navigazione.

I cookie possono essere:
• **Cookie di sessione**: vengono cancellati quando chiudi il browser
• **Cookie persistenti**: rimangono sul dispositivo per un periodo definito`
        },
        {
          title: "Cookie Utilizzati da SmartBalloon",
          content: `SmartBalloon utilizza **SOLO cookie essenziali** necessari per il funzionamento dell'applicazione:

| Nome | Tipo | Scopo | Durata |
|------|------|-------|--------|
| sb-access-token | Essenziale | Autenticazione Supabase | Sessione |
| sb-refresh-token | Essenziale | Rinnovo sessione | 7 giorni |
| cookie-consent | Essenziale | Memorizza preferenze cookie | 1 anno |
| i18nextLng | Funzionale | Lingua preferita | 1 anno |

**Non utilizziamo:**
• Cookie di tracciamento
• Cookie pubblicitari
• Cookie di profilazione
• Cookie di terze parti per analytics`
        },
        {
          title: "Cookie di Terze Parti",
          content: `I nostri fornitori di servizi potrebbero impostare i propri cookie:

**Stripe (pagamenti):**
Stripe può impostare cookie necessari per la sicurezza delle transazioni. Consulta la loro policy: https://stripe.com/cookies-policy

**Supabase (autenticazione):**
Cookie tecnici per gestire la sessione di autenticazione.

Questi cookie sono essenziali per il funzionamento dei rispettivi servizi.`
        },
        {
          title: "Come Gestire i Cookie",
          content: `Puoi controllare e gestire i cookie attraverso le impostazioni del tuo browser:

**Chrome:** Impostazioni > Privacy e sicurezza > Cookie
**Firefox:** Opzioni > Privacy e sicurezza > Cookie
**Safari:** Preferenze > Privacy > Cookie
**Edge:** Impostazioni > Privacy > Cookie

**Nota importante:** Disabilitare i cookie essenziali potrebbe compromettere il funzionamento dell'applicazione (es. non potrai effettuare il login).`
        },
        {
          title: "Modifiche alla Cookie Policy",
          content: `Ci riserviamo il diritto di aggiornare questa Cookie Policy. Eventuali modifiche saranno pubblicate su questa pagina con la data di aggiornamento.

Per domande sulla nostra Cookie Policy, contattaci:
**Email:** info@lscsrl.it`
        }
      ]
    },
    en: {
      title: "Cookie Policy",
      lastUpdate: "Last updated: December 2025",
      intro: `This Cookie Policy explains what cookies are, which cookies we use on SmartBalloon, and how you can manage them.`,
      sections: [
        {
          title: "What Are Cookies",
          content: `Cookies are small text files stored on your device (computer, tablet, smartphone) when you visit a website. They help remember your preferences, maintain your session, and improve your browsing experience.

Cookies can be:
• **Session cookies**: deleted when you close your browser
• **Persistent cookies**: remain on your device for a defined period`
        },
        {
          title: "Cookies Used by SmartBalloon",
          content: `SmartBalloon uses **ONLY essential cookies** necessary for the application to function:

| Name | Type | Purpose | Duration |
|------|------|---------|----------|
| sb-access-token | Essential | Supabase authentication | Session |
| sb-refresh-token | Essential | Session renewal | 7 days |
| cookie-consent | Essential | Stores cookie preferences | 1 year |
| i18nextLng | Functional | Preferred language | 1 year |

**We do NOT use:**
• Tracking cookies
• Advertising cookies
• Profiling cookies
• Third-party analytics cookies`
        },
        {
          title: "Third-Party Cookies",
          content: `Our service providers may set their own cookies:

**Stripe (payments):**
Stripe may set cookies necessary for transaction security. See their policy: https://stripe.com/cookies-policy

**Supabase (authentication):**
Technical cookies to manage the authentication session.

These cookies are essential for the respective services to function.`
        },
        {
          title: "How to Manage Cookies",
          content: `You can control and manage cookies through your browser settings:

**Chrome:** Settings > Privacy and security > Cookies
**Firefox:** Options > Privacy & Security > Cookies
**Safari:** Preferences > Privacy > Cookies
**Edge:** Settings > Privacy > Cookies

**Important note:** Disabling essential cookies may impair the application's functionality (e.g., you won't be able to log in).`
        },
        {
          title: "Changes to Cookie Policy",
          content: `We reserve the right to update this Cookie Policy. Any changes will be posted on this page with the update date.

For questions about our Cookie Policy, contact us:
**Email:** info@lscsrl.it`
        }
      ]
    }
  };

  const c = content[lang] || content.en;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {lang === 'it' ? 'Indietro' : 'Back'}
          </Button>
          <h1 className="text-xl font-bold text-slate-800">{c.title}</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Cookie className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">{c.title}</h2>
              <p className="text-sm text-slate-500">{c.lastUpdate}</p>
            </div>
          </div>

          <p className="text-slate-600 mb-8 text-lg">{c.intro}</p>

          <div className="space-y-8">
            {c.sections.map((section, index) => (
              <div key={index} className="border-b border-slate-100 pb-6 last:border-0">
                <h3 className="text-lg font-semibold text-slate-800 mb-3">{section.title}</h3>
                <div className="text-slate-600 whitespace-pre-line prose prose-sm max-w-none">
                  {section.content.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CookiesPage;
